import escapeHtml from "escape-html";
import type { EChartsOption } from "echarts";
import {
  buildAxisLabelStyle,
  buildTooltipStyle,
  buildTooltipValueStyle,
  CHART_FONT_FAMILY,
  chartGridLineColor,
  getChartTextColors,
} from "@/pages/CampaignOverviewPage/charts/chartHelpers";
import { getChartPalette } from "@/pages/CampaignOverviewPage/charts/chartPalette";

interface PillBarRow {
  id?: string;
  label: string;
  value: number;
}

interface PillBarOptions {
  isDark: boolean;
  labelWidth: number;
  rows: PillBarRow[];
  formatTooltip?: (row: PillBarRow) => string;
  formatValue: (value: number) => string;
}

// Spec v2 horizontal bars: 10px pill on a full-width gray track, 12px row gap.
const BAR_HEIGHT = 10;
const BAR_RADIUS = BAR_HEIGHT / 2;
const ROW_GAP = 12;
const COLUMN_GAP = 12;
const VALUE_COLUMN_WIDTH = 56;
const GRID_PADDING = 4;
const LABEL_FONT_SIZE = 13;

// One row's vertical pitch — callers size the chart container with this so
// bars keep the spec's 12px gap regardless of how many rows render.
const PILL_BAR_ROW_PITCH = BAR_HEIGHT + ROW_GAP;

export const pillBarChartHeight = (rowCount: number): number =>
  rowCount * PILL_BAR_ROW_PITCH + GRID_PADDING * 2;

const PX_PER_REM = 16;

// The same height as a CSS length, so the container scales with the root
// font size like the rest of the layout.
export const pillBarChartHeightRem = (rowCount: number): string =>
  `${pillBarChartHeight(rowCount) / PX_PER_REM}rem`;

const MILLION = 1_000_000;
const THOUSAND = 1_000;

// "6.15M" / "845K" — the value column is only 56px wide, so values abbreviate.
export const formatCompactImpressions = (value: number): string => {
  if (value >= MILLION) return `${(value / MILLION).toFixed(2)}M`;
  if (value >= THOUSAND) return `${Math.floor(value / THOUSAND)}K`;
  return String(value);
};

// Builds the label | bar (1fr) | value layout from the spec in ECharts. The
// right-aligned value column is a second, label-only category axis, because a
// series label can't be pinned to a fixed column.
export const buildPillBarOption = ({
  formatTooltip,
  formatValue,
  isDark,
  labelWidth,
  rows,
}: PillBarOptions): EChartsOption => {
  const axisLabelStyle = buildAxisLabelStyle(isDark);
  const { primary, secondary } = getChartTextColors(isDark);
  const valueStyle = buildTooltipValueStyle(isDark);
  const labels = rows.map(({ label }) => label);
  const hiddenAxisLine = {
    axisLine: { show: false },
    axisTick: { show: false },
    splitLine: { show: false },
  };

  return {
    animation: false,
    grid: {
      bottom: GRID_PADDING,
      left: labelWidth + COLUMN_GAP,
      right: VALUE_COLUMN_WIDTH + COLUMN_GAP,
      top: GRID_PADDING,
    },
    series: [
      {
        backgroundStyle: {
          borderRadius: BAR_RADIUS,
          color: chartGridLineColor(isDark),
        },
        barWidth: BAR_HEIGHT,
        data: rows.map(({ id, value }) => ({ id, value })),
        itemStyle: {
          borderRadius: BAR_RADIUS,
          color: getChartPalette(isDark).barGradientH,
        },
        showBackground: true,
        type: "bar",
      },
    ],
    tooltip: {
      ...buildTooltipStyle(isDark),
      formatter: (params: unknown) => {
        const { dataIndex } = params as { dataIndex: number };
        const row = rows[dataIndex];
        if (!row) return "";
        return (
          formatTooltip?.(row) ??
          `${escapeHtml(row.label)}: <b style="${valueStyle}">${formatValue(row.value)}</b>`
        );
      },
      trigger: "item",
    },
    xAxis: { max: "dataMax", min: 0, show: false, type: "value" },
    yAxis: [
      {
        ...hiddenAxisLine,
        axisLabel: {
          align: "left",
          color: primary,
          fontFamily: CHART_FONT_FAMILY,
          fontSize: LABEL_FONT_SIZE,
          fontWeight: 400,
          interval: 0,
          margin: labelWidth + COLUMN_GAP,
          overflow: "truncate",
          width: labelWidth,
        },
        data: labels,
        inverse: true,
        type: "category",
      },
      {
        ...hiddenAxisLine,
        axisLabel: {
          ...axisLabelStyle,
          align: "right",
          color: secondary,
          fontSize: LABEL_FONT_SIZE,
          fontWeight: 400,
          interval: 0,
          margin: VALUE_COLUMN_WIDTH,
        },
        data: rows.map(({ value }) => formatValue(value)),
        inverse: true,
        position: "right",
        type: "category",
      },
    ],
  };
};
